/**
 * CaisseLayoutDemo — Page de comparaison de 3 layouts alternatifs pour l'espace Admin Caisse.
 *
 * Objectif : montrer 3 directions visuelles pour désencombrer l'espace Caisse Desktop.
 * L'utilisateur choisit la piste qui lui plaît, je l'implémente ensuite pour de vrai.
 *
 * URL : /caisse-layouts (route de démo, non-liée à l'app réelle)
 */
import React, { useState } from "react";
import {
  LayoutGrid, Calculator, Printer, FileText, Building2, ShoppingCart,
  ClipboardList, ClipboardCheck, Truck, BookOpen, Activity, BarChart3,
  Receipt, Wallet, Coins, Package, Users, RefreshCw, MessageSquare,
  Settings, KeyRound, Sparkles, Globe, Sunrise, Search, Command, Menu,
  Briefcase, Boxes, TrendingUp, BookMarked, ShieldCheck, ChefHat,
  ChevronRight, ArrowLeft, Home,
} from "lucide-react";

// ── Données partagées entre les 3 layouts ────────────────────────────────
const FAMILIES = [
  {
    key: "operations", label: "Opérations", icon: Briefcase, hex: "#f59e0b", desc: "Tables, commandes, factures",
    items: [
      { value: "tables", label: "Tables", icon: LayoutGrid, hex: "#0d9488" },
      { value: "commande", label: "Prise de commandes", icon: Calculator, hex: "#f59e0b" },
      { value: "bons", label: "Bons", icon: Printer, hex: "#f97316", badge: 12 },
      { value: "invoices", label: "Factures", icon: FileText, hex: "#3b82f6" },
      { value: "proforma", label: "Proforma", icon: FileText, hex: "#2563eb" },
      { value: "locations", label: "Locations", icon: Building2, hex: "#a855f7" },
    ],
  },
  {
    key: "stock", label: "Stock & Appro", icon: Boxes, hex: "#0ea5e9", desc: "Achats, besoins, fournisseurs",
    items: [
      { value: "achats", label: "Achats", icon: ShoppingCart, hex: "#a855f7", badge: 3 },
      { value: "courses", label: "Appro Manager", icon: ShoppingCart, hex: "#f59e0b" },
      { value: "needs", label: "Besoins", icon: ClipboardList, hex: "#6366f1", badge: 4 },
      { value: "field_stock", label: "Point de stock", icon: ClipboardCheck, hex: "#10b981" },
      { value: "po", label: "Fournisseurs & BC", icon: Truck, hex: "#0ea5e9" },
    ],
  },
  {
    key: "finances", label: "Finances & Rapports", icon: TrendingUp, hex: "#10b981", desc: "Stats, journal, comptes",
    items: [
      { value: "journee", label: "Journée", icon: Sunrise, hex: "#f59e0b" },
      { value: "point_caisse", label: "Point de la Caisse", icon: Receipt, hex: "#10b981" },
      { value: "stats", label: "Statistiques", icon: BarChart3, hex: "#22c55e", badge: 2 },
      { value: "forecasts", label: "Journal", icon: BookOpen, hex: "#a855f7" },
      { value: "hebdo", label: "Faire le point", icon: BarChart3, hex: "#06b6d4" },
      { value: "activite", label: "Activité", icon: Activity, hex: "#10b981" },
      { value: "current-accounts", label: "Compte courant", icon: Wallet, hex: "#059669" },
      { value: "tips", label: "Pourboires", icon: Coins, hex: "#eab308" },
    ],
  },
  {
    key: "catalog", label: "Catalogue & Clients", icon: BookMarked, hex: "#ec4899", desc: "Produits, clients, notes",
    items: [
      { value: "products", label: "Produits", icon: Package, hex: "#a855f7" },
      { value: "clients", label: "Clients", icon: Users, hex: "#ec4899" },
      { value: "subscriptions", label: "Abonnements", icon: RefreshCw, hex: "#06b6d4" },
      { value: "instructions", label: "Notes", icon: MessageSquare, hex: "#14b8a6", badge: 1 },
    ],
  },
  {
    key: "kitchen", label: "Cuisine & Jeux", icon: ChefHat, hex: "#06b6d4", desc: "Recoupement IA",
    items: [
      { value: "recoupement", label: "Rapports Cuisine & Jeux", icon: Sparkles, hex: "#06b6d4", badge: 5 },
    ],
  },
  {
    key: "admin", label: "Administration", icon: ShieldCheck, hex: "#ef4444", desc: "Utilisateurs, audit, avis",
    items: [
      { value: "users", label: "Utilisateurs", icon: Settings, hex: "#ef4444" },
      { value: "guest-codes", label: "Codes Invité", icon: KeyRound, hex: "#f59e0b" },
      { value: "audit", label: "Audit", icon: ClipboardList, hex: "#f43f5e" },
      { value: "customer-reviews", label: "Avis clients", icon: MessageSquare, hex: "#ec4899", badge: 8 },
      { value: "site-notifications", label: "Notifs Site", icon: Globe, hex: "#10b981" },
    ],
  },
];

// ══════════════════════════════════════════════════════════════════════════
// LAYOUT A — Rail minimal 64px + panneau slide-out contextuel
// Idée : L'écran est libéré à 95%. Sidebar ultra-fine avec seulement 6 icônes.
//         Au hover/clic, un panneau apparaît à côté avec les items de la famille.
// ══════════════════════════════════════════════════════════════════════════
function LayoutA() {
  const [openFamily, setOpenFamily] = useState("operations");
  const [active, setActive] = useState("tables");
  const family = FAMILIES.find((f) => f.key === openFamily);

  return (
    <div className="w-full h-[700px] bg-slate-950 rounded-2xl overflow-hidden border border-slate-800 flex relative">
      {/* Rail 64px */}
      <div className="w-16 flex-shrink-0 flex flex-col bg-slate-900 border-r border-slate-800/60">
        <div className="h-14 flex items-center justify-center border-b border-slate-800/60">
          <div className="w-9 h-9 rounded-lg bg-amber-500 flex items-center justify-center">
            <span className="text-slate-900 font-black text-sm">M</span>
          </div>
        </div>
        <div className="flex-1 py-2 space-y-1 px-2">
          {FAMILIES.map((f) => {
            const Icon = f.icon;
            const isOpen = openFamily === f.key;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setOpenFamily(f.key)}
                onMouseEnter={() => setOpenFamily(f.key)}
                className="w-12 h-12 rounded-xl flex items-center justify-center transition-all group relative"
                style={{
                  background: isOpen ? `${f.hex}22` : "transparent",
                  border: `1px solid ${isOpen ? `${f.hex}44` : "transparent"}`,
                }}
              >
                <Icon className="w-5 h-5 transition" strokeWidth={2.2} style={{ color: isOpen ? f.hex : "#94a3b8" }} />
                {isOpen && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 rounded-r" style={{ background: f.hex }} />}
              </button>
            );
          })}
        </div>
        <div className="h-14 border-t border-slate-800/60 flex items-center justify-center">
          <button className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400 hover:text-white">
            <Search className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Panneau items */}
      <div className="w-64 flex-shrink-0 bg-slate-900/70 border-r border-slate-800/60 flex flex-col">
        <div className="px-4 pt-5 pb-3 border-b border-slate-800/60">
          <p className="text-slate-500 text-[10px] uppercase tracking-[0.15em] font-bold">Famille</p>
          <p className="text-white text-[17px] font-black tracking-tight mt-0.5" style={{ color: family.hex }}>
            {family.label}
          </p>
          <p className="text-slate-500 text-[11.5px] mt-0.5">{family.desc}</p>
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1">
          {family.items.map((it) => {
            const Icon = it.icon;
            const isActive = active === it.value;
            return (
              <button
                key={it.value}
                type="button"
                onClick={() => setActive(it.value)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition ${
                  isActive ? "bg-slate-800/80" : "hover:bg-slate-800/40"
                }`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={2.2} style={{ color: it.hex }} />
                <span className={`text-[13.5px] font-semibold flex-1 ${isActive ? "text-white" : "text-slate-300"}`}>
                  {it.label}
                </span>
                {it.badge && (
                  <span className="min-w-[20px] h-5 px-1.5 rounded bg-red-500 text-white text-[10.5px] font-mono font-bold flex items-center justify-center">
                    {it.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Zone contenu */}
      <div className="flex-1 flex flex-col">
        <div className="h-14 border-b border-slate-800/60 flex items-center justify-between px-6">
          <div className="flex items-center gap-2 text-slate-400 text-[13px]">
            <span>{family.label}</span>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-white font-semibold">{family.items.find((i) => i.value === active)?.label || "Tables"}</span>
          </div>
          <button className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800/70 border border-slate-700 text-slate-400 text-[12.5px]">
            <Command className="w-3.5 h-3.5" /> K
          </button>
        </div>
        <div className="flex-1 p-8 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: `${family.hex}18` }}>
              <LayoutGrid className="w-8 h-8" style={{ color: family.hex }} />
            </div>
            <p className="text-white text-2xl font-black tracking-tight">Zone Module</p>
            <p className="text-slate-500 text-sm mt-1">L&apos;écran principal est libéré à 95%</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// LAYOUT B — Top bar horizontale : familles en pills + sous-menu contextuel
// Idée : Zéro sidebar. Header épuré style Linear/Notion, focus 100% contenu.
// ══════════════════════════════════════════════════════════════════════════
function LayoutB() {
  const [openFamily, setOpenFamily] = useState("operations");
  const [active, setActive] = useState("tables");
  const family = FAMILIES.find((f) => f.key === openFamily);

  return (
    <div className="w-full h-[700px] bg-slate-950 rounded-2xl overflow-hidden border border-slate-800 flex flex-col relative">
      {/* Header 1 : logo + familles pills + recherche */}
      <div className="h-14 flex items-center gap-4 px-6 border-b border-slate-800/60 bg-slate-900/60">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center">
            <span className="text-slate-900 font-black text-sm">M</span>
          </div>
          <span className="text-white font-black tracking-tight text-[15px]">Caisse Pro</span>
        </div>
        <div className="w-px h-6 bg-slate-800" />
        <nav className="flex items-center gap-1">
          {FAMILIES.map((f) => {
            const Icon = f.icon;
            const isOpen = openFamily === f.key;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setOpenFamily(f.key)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-semibold transition"
                style={{
                  background: isOpen ? `${f.hex}18` : "transparent",
                  color: isOpen ? f.hex : "#94a3b8",
                  border: `1px solid ${isOpen ? `${f.hex}40` : "transparent"}`,
                }}
              >
                <Icon className="w-3.5 h-3.5" strokeWidth={2.4} />
                {f.label}
              </button>
            );
          })}
        </nav>
        <div className="flex-1" />
        <button className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800/70 border border-slate-700 text-slate-400 text-[12.5px]">
          <Search className="w-3.5 h-3.5" />
          <span>Rechercher…</span>
          <kbd className="text-[10px] px-1.5 py-0.5 rounded bg-slate-900 border border-slate-700 font-mono">⌘K</kbd>
        </button>
      </div>

      {/* Header 2 : sous-menu horizontal des items de la famille */}
      <div className="h-11 flex items-center gap-1 px-6 border-b border-slate-800/60 bg-slate-900/30 overflow-x-auto">
        {family.items.map((it) => {
          const Icon = it.icon;
          const isActive = active === it.value;
          return (
            <button
              key={it.value}
              type="button"
              onClick={() => setActive(it.value)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[12.5px] font-semibold whitespace-nowrap transition ${
                isActive ? "bg-slate-800/90 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800/40"
              }`}
              style={isActive ? { borderBottom: `2px solid ${it.hex}`, borderRadius: "8px 8px 0 0" } : {}}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={2.4} style={{ color: it.hex }} />
              {it.label}
              {it.badge && (
                <span className="min-w-[18px] h-4 px-1 rounded bg-red-500 text-white text-[9.5px] font-mono font-bold flex items-center justify-center">
                  {it.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Zone contenu */}
      <div className="flex-1 p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: `${family.hex}18` }}>
            <LayoutGrid className="w-8 h-8" style={{ color: family.hex }} />
          </div>
          <p className="text-white text-2xl font-black tracking-tight">Zone Module</p>
          <p className="text-slate-500 text-sm mt-1">Zéro sidebar — 100% contenu</p>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// LAYOUT C — Hub d'accueil grand format + rail rétracté
// Idée : Une "home page" avec 6 grosses tuiles familles au centre.
//         Une fois dans un module → mini rail 48px + breadcrumb.
// ══════════════════════════════════════════════════════════════════════════
function LayoutC() {
  const [openFamily, setOpenFamily] = useState(null); // null = hub, sinon key
  const [active, setActive] = useState(null);
  const family = openFamily ? FAMILIES.find((f) => f.key === openFamily) : null;

  const goHome = () => { setOpenFamily(null); setActive(null); };

  return (
    <div className="w-full h-[700px] bg-slate-950 rounded-2xl overflow-hidden border border-slate-800 flex relative">
      {/* Mini rail 48px — visible seulement quand on est dans une famille */}
      {openFamily && (
        <div className="w-14 flex-shrink-0 flex flex-col bg-slate-900 border-r border-slate-800/60">
          <button onClick={goHome} className="h-14 flex items-center justify-center border-b border-slate-800/60 hover:bg-slate-800/50" title="Retour au Hub">
            <Home className="w-4 h-4 text-slate-400" />
          </button>
          <div className="flex-1 py-2 space-y-1 px-1.5">
            {FAMILIES.map((f) => {
              const Icon = f.icon;
              const isOpen = openFamily === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => { setOpenFamily(f.key); setActive(null); }}
                  className="w-11 h-11 rounded-lg flex items-center justify-center transition"
                  style={{
                    background: isOpen ? `${f.hex}22` : "transparent",
                  }}
                >
                  <Icon className="w-4 h-4" strokeWidth={2.4} style={{ color: isOpen ? f.hex : "#64748b" }} />
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="h-14 flex items-center justify-between px-6 border-b border-slate-800/60 bg-slate-900/40">
          {openFamily ? (
            <div className="flex items-center gap-2 text-[13px]">
              <button onClick={goHome} className="text-slate-500 hover:text-white transition">Hub</button>
              <ChevronRight className="w-3.5 h-3.5 text-slate-600" />
              <span className="font-black" style={{ color: family.hex }}>{family.label}</span>
              {active && (
                <>
                  <ChevronRight className="w-3.5 h-3.5 text-slate-600" />
                  <span className="text-white font-semibold">{family.items.find((i) => i.value === active)?.label}</span>
                </>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-amber-500 flex items-center justify-center">
                <span className="text-slate-900 font-black text-base">M</span>
              </div>
              <div>
                <p className="text-white font-black tracking-tight leading-tight text-[15px]">Caisse Pro</p>
                <p className="text-slate-500 text-[10.5px] uppercase tracking-[0.15em] font-bold leading-tight">Tableau de bord</p>
              </div>
            </div>
          )}
          <button className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800/70 border border-slate-700 text-slate-400 text-[12.5px]">
            <Command className="w-3.5 h-3.5" /> K
          </button>
        </div>

        {/* Body : soit Hub (grosses tuiles), soit vue module */}
        {!openFamily ? (
          <div className="flex-1 overflow-y-auto p-10">
            <p className="text-slate-500 text-[11px] uppercase tracking-[0.18em] font-bold mb-6">Sélectionnez une catégorie</p>
            <div className="grid grid-cols-3 gap-5 max-w-4xl">
              {FAMILIES.map((f) => {
                const Icon = f.icon;
                const totalBadges = f.items.reduce((a, i) => a + (i.badge || 0), 0);
                return (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setOpenFamily(f.key)}
                    className="relative flex flex-col items-start gap-3 p-5 rounded-2xl bg-slate-900/60 border border-slate-800 hover:border-slate-700 hover:bg-slate-800/60 transition text-left group"
                    style={{ borderLeftWidth: "3px", borderLeftColor: f.hex }}
                  >
                    {totalBadges > 0 && (
                      <span className="absolute top-3 right-3 min-w-[22px] h-[22px] px-1.5 rounded-md bg-red-500 text-white text-[11px] font-mono font-bold flex items-center justify-center">
                        {totalBadges}
                      </span>
                    )}
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${f.hex}22` }}>
                      <Icon className="w-6 h-6" strokeWidth={2.2} style={{ color: f.hex }} />
                    </div>
                    <div>
                      <p className="text-white font-black text-[16px] tracking-tight">{f.label}</p>
                      <p className="text-slate-500 text-[12px] mt-0.5">{f.desc}</p>
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-500 text-[11px] font-bold uppercase tracking-wider">
                      <span>{f.items.length} module{f.items.length > 1 ? "s" : ""}</span>
                      <ChevronRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : !active ? (
          /* Vue famille = liste horizontale des modules en tuiles */
          <div className="flex-1 overflow-y-auto p-8">
            <div className="max-w-4xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${family.hex}22` }}>
                  {React.createElement(family.icon, { className: "w-5 h-5", strokeWidth: 2.4, style: { color: family.hex } })}
                </div>
                <div>
                  <p className="text-white text-2xl font-black tracking-tight" style={{ color: family.hex }}>{family.label}</p>
                  <p className="text-slate-500 text-[13px]">{family.desc}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {family.items.map((it) => {
                  const Icon = it.icon;
                  return (
                    <button
                      key={it.value}
                      type="button"
                      onClick={() => setActive(it.value)}
                      className="relative flex items-center gap-3 p-3.5 rounded-xl bg-slate-900/50 border border-slate-800 hover:border-slate-700 hover:bg-slate-800/70 transition text-left"
                    >
                      {it.badge && (
                        <span className="absolute top-2 right-2 min-w-[18px] h-[18px] px-1 rounded bg-red-500 text-white text-[10px] font-mono font-bold flex items-center justify-center">
                          {it.badge}
                        </span>
                      )}
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${it.hex}22` }}>
                        <Icon className="w-5 h-5" strokeWidth={2.2} style={{ color: it.hex }} />
                      </div>
                      <span className="text-white text-[13px] font-bold tracking-tight">{it.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          /* Vue module actif */
          <div className="flex-1 p-8 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: `${family.hex}18` }}>
                <LayoutGrid className="w-8 h-8" style={{ color: family.hex }} />
              </div>
              <p className="text-white text-2xl font-black tracking-tight">Zone Module</p>
              <p className="text-slate-500 text-sm mt-1">Hub → catégorie → module</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Page hôte : sélecteur en haut, aperçu du layout choisi en dessous
// ══════════════════════════════════════════════════════════════════════════
const LAYOUTS = [
  {
    id: "A",
    title: "A — Rail minimal + panneau latéral",
    tagline: "Sidebar 64px avec 6 icônes de familles seulement. Panneau contextuel de 256px se déploie au survol/clic. Écran principal libéré à 95%.",
    pros: ["Sidebar ultra-fine, ne mange pas d'écran", "Un seul clic pour voir tous les items d'une famille", "Idéal grands écrans"],
    cons: ["Nécessite un peu d'apprentissage (icônes seules)"],
    Component: LayoutA,
  },
  {
    id: "B",
    title: "B — Top bar horizontale (Linear-style)",
    tagline: "Zéro sidebar. Familles en pills en haut, items de la famille active juste en dessous. Focus total sur le contenu.",
    pros: ["Aucune surface latérale perdue", "Très lisible : tout est étiqueté texte", "Style pro type Linear / Notion / Vercel"],
    cons: ["Peut devenir dense si beaucoup d'items dans une famille"],
    Component: LayoutB,
  },
  {
    id: "C",
    title: "C — Hub d'accueil + mini rail",
    tagline: "Page d'accueil avec 6 grosses tuiles familles au centre. Une fois dans un module, mini rail 48px reste visible pour switcher.",
    pros: ["Extrêmement aéré à l'ouverture", "Excellente hiérarchie visuelle (Hub → Famille → Module)", "Idéal pour vue d'ensemble rapide"],
    cons: ["2 clics pour arriver à un module (au lieu d'un)"],
    Component: LayoutC,
  },
];

export default function CaisseLayoutDemo() {
  const [selected, setSelected] = useState("A");
  const layout = LAYOUTS.find((l) => l.id === selected);
  const Preview = layout.Component;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900 text-white p-6 md:p-10">
      <style>{`
        .clayout, .clayout * { font-family: 'Space Grotesk', 'Outfit', system-ui, sans-serif; }
      `}</style>
      <div className="clayout max-w-7xl mx-auto">
        <header className="mb-8">
          <p className="text-amber-400 text-[11px] uppercase tracking-[0.18em] font-bold mb-2">Comparaison — Démo interne</p>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight text-white">3 pistes pour désencombrer l&apos;espace Caisse Desktop</h1>
          <p className="text-slate-400 text-[14.5px] mt-3 max-w-3xl">
            Cliquez sur une piste pour voir un aperçu interactif. Toutes les cartes sont cliquables dans les maquettes pour vous laisser vraiment sentir la navigation.
          </p>
        </header>

        {/* Sélecteur de layout */}
        <div className="grid md:grid-cols-3 gap-3 mb-8">
          {LAYOUTS.map((l) => {
            const isSelected = selected === l.id;
            return (
              <button
                key={l.id}
                type="button"
                onClick={() => setSelected(l.id)}
                className={`relative flex flex-col items-start gap-2 p-5 rounded-2xl border text-left transition ${
                  isSelected
                    ? "bg-slate-800/80 border-amber-500/60 ring-2 ring-amber-500/30"
                    : "bg-slate-900/60 border-slate-800 hover:border-slate-700 hover:bg-slate-800/50"
                }`}
              >
                <div className="flex items-center gap-2.5 w-full">
                  <span className={`w-9 h-9 rounded-lg flex items-center justify-center font-black text-lg ${
                    isSelected ? "bg-amber-500 text-slate-900" : "bg-slate-800 text-slate-400"
                  }`}>
                    {l.id}
                  </span>
                  <span className="text-white font-black text-[15px] tracking-tight flex-1">{l.title.replace(`${l.id} — `, "")}</span>
                </div>
                <p className="text-slate-400 text-[12.5px] leading-relaxed">{l.tagline}</p>
              </button>
            );
          })}
        </div>

        {/* Aperçu du layout sélectionné */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            <p className="text-slate-500 text-[11px] uppercase tracking-[0.15em] font-bold">Aperçu</p>
            <h2 className="text-white text-2xl font-black tracking-tight">{layout.title}</h2>
          </div>
        </div>
        <Preview />

        {/* Pros / Cons */}
        <div className="grid md:grid-cols-2 gap-4 mt-6">
          <div className="p-5 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
            <p className="text-emerald-400 text-[11px] uppercase tracking-[0.15em] font-bold mb-2">Points forts</p>
            <ul className="space-y-1.5">
              {layout.pros.map((p, i) => (
                <li key={i} className="text-slate-200 text-[13.5px] flex items-start gap-2">
                  <span className="text-emerald-400 mt-0.5">✓</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="p-5 rounded-xl bg-amber-500/5 border border-amber-500/20">
            <p className="text-amber-400 text-[11px] uppercase tracking-[0.15em] font-bold mb-2">À noter</p>
            <ul className="space-y-1.5">
              {layout.cons.map((p, i) => (
                <li key={i} className="text-slate-200 text-[13.5px] flex items-start gap-2">
                  <span className="text-amber-400 mt-0.5">⚠</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-10 p-5 rounded-2xl bg-slate-900/70 border border-slate-800 text-center">
          <p className="text-slate-400 text-[13.5px]">
            Une fois votre piste choisie, dites-moi simplement <span className="text-white font-black">« Je prends la A »</span> (ou B, ou C)
            et je l&apos;implémente proprement dans <span className="text-amber-400 font-semibold">CaissePage</span> en remplacement de la sidebar actuelle.
          </p>
        </div>
      </div>
    </div>
  );
}
